import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ethers, toBigInt } from 'ethers';
import { useAccount, useWriteContract } from 'wagmi';
import { CPGABI, ERC20ABI, TaskEscrowABI } from '../../../ABI/ABI';
import { JOB_ESCROW_FN } from '../../../utils/jobsDiamond';
import ThemedSelect from '../../../components/ui/ThemedSelect';
import BackendWalletSignInPanel from '../../../components/auth/BackendWalletSignInPanel';
import DisputeMessageThread from '../../../components/dispute/DisputeMessageThread';
import Loader from '../../../components/loader/Loader';
import {
    ensureAdminBackendSession,
    getAdminDisputeById,
    getStoredBackendToken,
    resolveTaskDispute,
    updateDisputeStatus,
} from '../../../api/backendClient';
import { useBackendWalletAuth } from '../../../hooks/useBackendWalletAuth';
import { JOBS_PORTAL_PATH } from '../../../config/paths';
import {
    CPGAddress,
    TASK_ESCROW_ADDRESS,
    targetChainId,
    WalletAddressContext,
} from '../../../constant/constant';
import { useTheme } from '../../../context/ThemeContext';
import { resolveAttachmentUrl } from '../../../utils/attachmentUrl';
import { waitForSuccessfulTransactionReceipt } from '../../../utils/transactionReceipt';
import {
    fireErrorToast,
    fireInfoToast,
    fireSuccessToast,
} from '../../../utils/commonFunction';
import { readContractData } from '../../../utils/contractInstance';
import { fetchPaymentDisputeEscrowContext } from '../../../utils/cpgDiamondReader';
import {
    DISPUTE_CATEGORY_JOB,
    DISPUTE_CATEGORY_PAYMENT,
    formatDisputeCategoryLabel,
    isJobDispute,
    JOB_DISPUTE_ACCENT,
} from './adminDisputeUtils';

const statusLabel = (status) => {
    const key = String(status || '').toLowerCase();
    if (key === 'pending') return 'Pending';
    if (key === 'in_review') return 'In Review';
    if (key === 'resolved') return 'Resolved';
    if (key === 'closed') return 'Closed';
    return status || '—';
};

const ResolutionGuide = ({ items }) => (
    <div className="mt-4 rounded-xl border border-zinc-200/90 bg-zinc-50/80 px-4 py-3 dark:border-zinc-700/80 dark:bg-zinc-950/40">
        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            How to choose
        </p>
        <ul className="mt-2 space-y-2.5 text-sm text-zinc-600 dark:text-zinc-400">
            {items.map((item) => (
                <li key={item.title} className="flex gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-500 dark:bg-teal-400" />
                    <div>
                        <p className="font-medium text-zinc-800 dark:text-zinc-200">
                            {item.title}
                        </p>
                        <p className="mt-0.5">{item.body}</p>
                    </div>
                </li>
            ))}
        </ul>
    </div>
);

const DetailField = ({ label, children, className = '' }) => (
    <div className={className}>
        <label className="mb-1 block text-sm font-medium text-zinc-600 dark:text-zinc-400">
            {label}
        </label>
        <div className="rounded-lg bg-zinc-100 px-3 py-2 text-zinc-900 dark:bg-zinc-800/80 dark:text-zinc-100">
            {children}
        </div>
    </div>
);

const AdminDisputeDetailPage = () => {
    const { disputeId } = useParams();
    const location = useLocation();
    const { isAdmin } = useContext(WalletAddressContext);
    const { address: walletAddress, chainId } = useAccount();
    const { writeContractAsync } = useWriteContract();
    const { resolvedTheme } = useTheme();
    const {
        status: authStatus,
        error: authError,
        signIn,
        isReady: isBackendReady,
        token: backendToken,
    } = useBackendWalletAuth({
        walletAddress,
        rolePreference: 'employee',
        enabled: Boolean(walletAddress),
    });

    const escrowAddress = TASK_ESCROW_ADDRESS?.trim();
    const escrowConfigured = Boolean(
        escrowAddress && ethers.isAddress(escrowAddress),
    );

    const listCategory = location.state?.category;
    const backHref =
        listCategory === DISPUTE_CATEGORY_JOB ||
        listCategory === DISPUTE_CATEGORY_PAYMENT
            ? `/admin-dispute?category=${listCategory}`
            : '/admin-dispute';

    const [dispute, setDispute] = useState(location.state?.dispute || null);
    const [loading, setLoading] = useState(!location.state?.dispute);
    const [onChainResolving, setOnChainResolving] = useState(null);
    const [disputeThreadToken, setDisputeThreadToken] = useState('');
    const [disputeThreadAuthReady, setDisputeThreadAuthReady] = useState(false);
    const [adminNote, setAdminNote] = useState('');
    const [paymentResolutionMode, setPaymentResolutionMode] = useState('full');
    const [escrowContext, setEscrowContext] = useState(null);
    const [escrowLoading, setEscrowLoading] = useState(false);
    const [splitToSender, setSplitToSender] = useState('');
    const [splitToReceiver, setSplitToReceiver] = useState('');

    const getAdminAuthToken = useCallback(async () => {
        if (!walletAddress) return null;
        if (backendToken) return backendToken;
        return (
            getStoredBackendToken(walletAddress) ||
            (await ensureAdminBackendSession(walletAddress))
        );
    }, [walletAddress, backendToken]);

    const loadDispute = useCallback(async () => {
        if (!disputeId || !isBackendReady) return;
        try {
            setLoading(true);
            const token = await getAdminAuthToken();
            if (!token) {
                fireErrorToast(
                    'Could not sign in to the admin dispute API. Approve the wallet signature request, then try again.',
                );
                return;
            }
            const data = await getAdminDisputeById(token, disputeId);
            setDispute(data || null);
        } catch (error) {
            const msg =
                error?.response?.data?.message ||
                error?.message ||
                'Failed to load dispute';
            fireErrorToast(msg);
            setDispute(null);
        } finally {
            setLoading(false);
        }
    }, [disputeId, getAdminAuthToken, isBackendReady]);

    useEffect(() => {
        if (!location.state?.dispute && isBackendReady) {
            loadDispute();
        }
    }, [loadDispute, location.state?.dispute, isBackendReady]);

    useEffect(() => {
        let cancelled = false;

        const syncBackendRoleForDisputeThread = async () => {
            if (!walletAddress) {
                setDisputeThreadToken('');
                setDisputeThreadAuthReady(true);
                return;
            }

            if (!isAdmin || !isBackendReady) {
                setDisputeThreadToken(
                    backendToken || getStoredBackendToken(walletAddress) || '',
                );
                setDisputeThreadAuthReady(Boolean(isAdmin) ? isBackendReady : true);
                return;
            }

            setDisputeThreadAuthReady(false);
            try {
                const token = await getAdminAuthToken();
                if (cancelled) return;
                setDisputeThreadToken(token || '');
            } catch (error) {
                if (!cancelled) {
                    fireErrorToast(
                        error?.response?.data?.message ||
                            'Could not refresh admin session for discussion',
                    );
                    setDisputeThreadToken(
                        getStoredBackendToken(walletAddress) || '',
                    );
                }
            } finally {
                if (!cancelled) {
                    setDisputeThreadAuthReady(true);
                }
            }
        };

        syncBackendRoleForDisputeThread();
        return () => {
            cancelled = true;
        };
    }, [walletAddress, isAdmin, isBackendReady, backendToken, getAdminAuthToken]);

    const loadEscrowContext = useCallback(async () => {
        if (!dispute?.paymentId || isJobDispute(dispute)) {
            setEscrowContext(null);
            return;
        }
        try {
            setEscrowLoading(true);
            const context = await fetchPaymentDisputeEscrowContext(
                readContractData,
                CPGAddress,
                CPGABI,
                dispute.paymentId,
                ERC20ABI,
            );
            setEscrowContext(context);
            if (context?.remainingFormatted) {
                setSplitToSender('');
                setSplitToReceiver(context.remainingFormatted);
            }
        } catch (_error) {
            setEscrowContext(null);
        } finally {
            setEscrowLoading(false);
        }
    }, [dispute]);

    useEffect(() => {
        if (dispute && !isJobDispute(dispute) && dispute.paymentId) {
            void loadEscrowContext();
        }
    }, [dispute, loadEscrowContext]);

    const handleSplitSenderChange = (value) => {
        setSplitToSender(value);
        if (!escrowContext?.remainingFormatted) return;
        const remaining = Number(escrowContext.remainingFormatted);
        const sender = Number(value);
        if (!Number.isFinite(remaining) || !Number.isFinite(sender) || sender < 0) {
            return;
        }
        const receiver = Math.max(0, remaining - sender);
        setSplitToReceiver(
            Number.isFinite(receiver) ? String(Number(receiver.toFixed(8))) : '',
        );
    };

    const handleSplitReceiverChange = (value) => {
        setSplitToReceiver(value);
        if (!escrowContext?.remainingFormatted) return;
        const remaining = Number(escrowContext.remainingFormatted);
        const receiver = Number(value);
        if (!Number.isFinite(remaining) || !Number.isFinite(receiver) || receiver < 0) {
            return;
        }
        const sender = Math.max(0, remaining - receiver);
        setSplitToSender(
            Number.isFinite(sender) ? String(Number(sender.toFixed(8))) : '',
        );
    };

    const handlePaymentResolveOnChain = async (isApprove) => {
        if (!dispute?.disputeId && dispute?.disputeId !== 0) {
            fireErrorToast(
                'Dispute ID not synced yet. Wait for backend sync/cron and retry.',
            );
            return;
        }
        if (chainId !== targetChainId) {
            fireErrorToast('Switch to the correct network before resolving on-chain.');
            return;
        }

        const pendingKey = isApprove ? 'payment-approve' : 'payment-reject';
        try {
            setOnChainResolving(pendingKey);
            const txHash = await writeContractAsync({
                address: CPGAddress,
                abi: CPGABI,
                functionName: 'resolveDispute',
                args: [toBigInt(dispute.disputeId), isApprove],
                chainId: targetChainId,
            });
            await waitForSuccessfulTransactionReceipt(txHash);

            const token = await getAdminAuthToken();
            const nextStatus = isApprove ? 'resolved' : 'closed';
            await updateDisputeStatus(token, dispute.id, nextStatus);
            fireInfoToast(
                isApprove
                    ? 'Dispute approved on-chain'
                    : 'Dispute rejected on-chain',
            );
            setDispute((prev) =>
                prev ? { ...prev, status: nextStatus } : prev,
            );
        } catch (e) {
            fireErrorToast(
                e?.shortMessage ||
                    e?.message ||
                    e?.response?.data?.message ||
                    'On-chain resolve failed or was rejected',
            );
        } finally {
            setOnChainResolving(null);
        }
    };

    const handlePaymentResolveWithSplit = async () => {
        if (!dispute?.disputeId && dispute?.disputeId !== 0) {
            fireErrorToast(
                'Dispute ID not synced yet. Wait for backend sync/cron and retry.',
            );
            return;
        }
        if (!escrowContext?.remainingWei) {
            fireErrorToast('Could not load remaining escrow from chain.');
            return;
        }
        if (chainId !== targetChainId) {
            fireErrorToast('Switch to the correct network before resolving on-chain.');
            return;
        }

        const decimals = escrowContext.decimals ?? 6;
        let toSenderWei;
        let toReceiverWei;
        try {
            toSenderWei = ethers.parseUnits(
                String(splitToSender || '0'),
                decimals,
            );
            toReceiverWei = ethers.parseUnits(
                String(splitToReceiver || '0'),
                decimals,
            );
        } catch (_error) {
            fireErrorToast('Enter valid split amounts for sender and receiver.');
            return;
        }

        const remainingWei = BigInt(escrowContext.remainingWei);
        if (toSenderWei + toReceiverWei !== remainingWei) {
            fireErrorToast(
                `Split must equal remaining escrow (${escrowContext.remainingFormatted}).`,
            );
            return;
        }
        if (toSenderWei === 0n && toReceiverWei === 0n) {
            fireErrorToast('At least one party must receive funds.');
            return;
        }

        try {
            setOnChainResolving('payment-split');
            const txHash = await writeContractAsync({
                address: CPGAddress,
                abi: CPGABI,
                functionName: 'resolveDisputeWithSplit',
                args: [toBigInt(dispute.disputeId), toSenderWei, toReceiverWei],
                chainId: targetChainId,
            });
            await waitForSuccessfulTransactionReceipt(txHash);

            const token = await getAdminAuthToken();
            await updateDisputeStatus(token, dispute.id, 'resolved');
            fireSuccessToast('Dispute resolved on-chain with custom split.');
            setDispute((prev) =>
                prev ? { ...prev, status: 'resolved', resolutionTxHash: txHash } : prev,
            );
        } catch (e) {
            fireErrorToast(
                e?.shortMessage ||
                    e?.message ||
                    e?.response?.data?.message ||
                    'On-chain split resolve failed or was rejected',
            );
        } finally {
            setOnChainResolving(null);
        }
    };

    const handleStatusChange = async (nextStatus) => {
        if (!dispute?.id) return;
        try {
            const token = await getAdminAuthToken();
            await updateDisputeStatus(token, dispute.id, nextStatus);
            fireInfoToast('Status updated successfully');
            setDispute((prev) =>
                prev ? { ...prev, status: nextStatus } : prev,
            );
        } catch (_error) {
            fireErrorToast('Failed to update status');
        }
    };

    const handleMarkInReview = async () => {
        if (!dispute?.id) return;
        await handleStatusChange('in_review');
    };

    const handleJobResolve = async (favorEmployee) => {
        if (!dispute?.taskId) {
            fireErrorToast('Task ID missing. Open this dispute in the Jobs portal.');
            return;
        }
        if (dispute.taskEscrowId == null || dispute.taskEscrowId === '') {
            fireErrorToast(
                'Escrow ID missing. The task may not be funded on-chain yet.',
            );
            return;
        }
        if (!escrowConfigured) {
            fireErrorToast('Task escrow contract address is not configured.');
            return;
        }
        if (chainId !== targetChainId) {
            fireErrorToast('Switch to the correct network before resolving on-chain.');
            return;
        }
        const pendingKey = favorEmployee ? 'job-employee' : 'job-company';
        try {
            setOnChainResolving(pendingKey);
            const txHash = await writeContractAsync({
                address: escrowAddress,
                abi: TaskEscrowABI,
                functionName: JOB_ESCROW_FN.resolveJobDispute,
                args: [Number(dispute.taskEscrowId), favorEmployee],
                chainId: targetChainId,
            });
            await waitForSuccessfulTransactionReceipt(txHash);

            const token = await getAdminAuthToken();
            const data = await resolveTaskDispute(token, dispute.taskId, {
                favorEmployee,
                resolutionNote: adminNote.trim(),
                resolveTxHash: txHash,
            });
            fireSuccessToast(
                favorEmployee
                    ? 'Dispute resolved. Payment released to the employee.'
                    : 'Dispute resolved. Funds returned to the company.',
            );
            setAdminNote('');
            const resolved = data?.dispute || data;
            if (resolved) {
                setDispute((prev) => ({
                    ...prev,
                    ...resolved,
                    resolutionTxHash: txHash,
                }));
            } else {
                await loadDispute();
            }
        } catch (e) {
            fireErrorToast(
                e?.shortMessage ||
                    e?.message ||
                    e?.response?.data?.message ||
                    'On-chain resolve failed or was rejected',
            );
        } finally {
            setOnChainResolving(null);
        }
    };

    const isDark = resolvedTheme === 'dark';
    const jobDispute = dispute && isJobDispute(dispute);
    const isResolved =
        dispute &&
        ['resolved', 'closed'].includes(
            String(dispute.status || '').toLowerCase(),
        );

    if (walletAddress && !isBackendReady) {
        return (
            <div className="crypto-page relative min-h-[calc(100vh-80px)] overflow-hidden">
                <BackendWalletSignInPanel
                    walletAddress={walletAddress}
                    status={authStatus}
                    error={authError}
                    onRetry={signIn}
                    title="Sign in for admin disputes"
                    description="Sign a free message to verify your admin wallet and open this dispute case."
                />
            </div>
        );
    }

    if (loading && !dispute) {
        return (
            <div className="crypto-page relative flex min-h-[calc(100vh-80px)] items-center justify-center">
                <Loader size="40px" stroke="#0d9488" />
            </div>
        );
    }

    if (!dispute) {
        return (
            <div className="crypto-page relative min-h-[calc(100vh-80px)]">
                <div className="relative z-10 mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
                    <p className="text-zinc-600 dark:text-zinc-400">
                        Dispute not found or you do not have access.
                    </p>
                    <Link
                        to={backHref}
                        className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-teal-700 hover:text-teal-600 dark:text-teal-400"
                    >
                        <i className="fa-solid fa-arrow-left" aria-hidden />
                        Back to dispute queue
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="crypto-page relative min-h-[calc(100vh-80px)] overflow-hidden">
            <div className="relative z-10 mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
                <div className="mb-8">
                    <Link
                        to={backHref}
                        className="inline-flex items-center gap-2 text-sm font-medium text-zinc-600 transition-colors hover:text-teal-700 dark:text-zinc-400 dark:hover:text-teal-400"
                    >
                        <i className="fa-solid fa-arrow-left" aria-hidden />
                        Back to dispute queue
                    </Link>
                </div>

                <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <p className="mb-2 text-xs font-bold uppercase tracking-[0.3em] text-teal-700 dark:text-teal-400">
                            Admin · Dispute review
                        </p>
                        <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 sm:text-3xl">
                            Dispute details
                        </h1>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            <span
                                className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${
                                    jobDispute
                                        ? JOB_DISPUTE_ACCENT.categoryBadge
                                        : 'border-teal-300 bg-teal-50 text-teal-900 dark:border-teal-700 dark:bg-teal-950/50 dark:text-teal-200'
                                }`}
                            >
                                {formatDisputeCategoryLabel(dispute)}
                            </span>
                            <span className="rounded-full border border-zinc-300 bg-zinc-50 px-2.5 py-1 text-xs font-medium text-zinc-700 dark:border-zinc-600 dark:bg-zinc-800/60 dark:text-zinc-300">
                                {statusLabel(dispute.status)}
                            </span>
                        </div>
                    </div>
                </header>

                <section
                    className="mb-8 rounded-2xl border border-teal-200/80 bg-gradient-to-br from-teal-50/80 via-white to-white p-5 shadow-sm dark:border-teal-800/40 dark:from-teal-950/20 dark:via-zinc-900/90 dark:to-zinc-900/90"
                    aria-label="Admin actions"
                >
                    <p className="text-xs font-bold uppercase tracking-wider text-teal-800 dark:text-teal-300">
                        Admin actions
                    </p>
                    {isResolved ? (
                        <div className="mt-2">
                            <p className="text-sm text-zinc-600 dark:text-zinc-400">
                                This dispute is{' '}
                                {statusLabel(dispute.status).toLowerCase()}. On-chain
                                resolution is no longer available here.
                            </p>
                            {jobDispute && dispute.taskId ? (
                                <Link
                                    to={`${JOBS_PORTAL_PATH}?section=workspace&taskId=${dispute.taskId}&panel=dispute`}
                                    className="mt-4 inline-flex rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-900 transition hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-200 dark:hover:bg-amber-950/70"
                                >
                                    View in Jobs portal
                                </Link>
                            ) : null}
                        </div>
                    ) : jobDispute ? (
                        <>
                            <p className="mt-2 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                                Review the dispute details and discussion, then
                                settle the escrow. Your wallet will ask you to confirm;
                                the case is updated only after the transaction succeeds.
                            </p>
                            <textarea
                                value={adminNote}
                                onChange={(e) => setAdminNote(e.target.value)}
                                rows={2}
                                placeholder="Add an internal resolution note (optional)"
                                className="cpg-input mt-4 min-h-[64px] w-full resize-y"
                            />
                            <div className="mt-4 flex flex-wrap gap-2">
                                {dispute.status === 'pending' ? (
                                    <button
                                        type="button"
                                        onClick={handleMarkInReview}
                                        disabled={onChainResolving !== null}
                                        className="rounded-lg border border-zinc-200 bg-white px-4 py-2.5 text-sm font-semibold text-zinc-800 transition hover:bg-zinc-50 disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
                                    >
                                        Start review
                                    </button>
                                ) : null}
                                <button
                                    type="button"
                                    disabled={onChainResolving !== null}
                                    onClick={() => handleJobResolve(true)}
                                    className="rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-500 disabled:opacity-60"
                                >
                                    {onChainResolving === 'job-employee'
                                        ? 'Waiting for wallet…'
                                        : 'Release payment to employee'}
                                </button>
                                <button
                                    type="button"
                                    disabled={onChainResolving !== null}
                                    onClick={() => handleJobResolve(false)}
                                    className="rounded-lg bg-zinc-800 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-zinc-900 disabled:opacity-60 dark:bg-zinc-700 dark:hover:bg-zinc-600"
                                >
                                    {onChainResolving === 'job-company'
                                        ? 'Waiting for wallet…'
                                        : 'Refund company'}
                                </button>
                                {dispute.taskId ? (
                                    <Link
                                        to={`${JOBS_PORTAL_PATH}?section=workspace&taskId=${dispute.taskId}&panel=dispute`}
                                        className={`rounded-lg border px-4 py-2.5 text-sm font-semibold transition ${JOB_DISPUTE_ACCENT.linkButton}`}
                                    >
                                        View task in Jobs portal
                                    </Link>
                                ) : null}
                            </div>
                            <ResolutionGuide
                                items={[
                                    ...(dispute.status === 'pending'
                                        ? [
                                              {
                                                  title: 'Start review',
                                                  body: 'Marks the case as under admin review. No funds move; use while you are still gathering evidence.',
                                              },
                                          ]
                                        : []),
                                    {
                                        title: 'Release payment to employee',
                                        body: 'Closes the dispute in the employee’s favor and sends escrowed funds to the employee. Choose when deliverables meet the agreed scope.',
                                    },
                                    {
                                        title: 'Refund company',
                                        body: 'Closes the dispute in the company’s favor and returns escrowed funds to the company. Choose when work was not accepted or a refund is warranted.',
                                    },
                                    ...(dispute.taskId
                                        ? [
                                              {
                                                  title: 'View task in Jobs portal',
                                                  body: 'Opens the full task view with submissions, proofs, and the dispute thread before you finalize.',
                                              },
                                          ]
                                        : []),
                                ]}
                            />
                        </>
                    ) : (
                        <>
                            <p className="mt-2 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                                Resolve this tracked payment dispute on-chain. Use a
                                full refund or release, or split the remaining escrow
                                between sender and receiver.
                            </p>

                            <div className="mt-4 flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={() => setPaymentResolutionMode('full')}
                                    className={`rounded-xl border px-3 py-1.5 text-[11px] font-semibold transition-all ${
                                        paymentResolutionMode === 'full'
                                            ? 'border-teal-600 bg-teal-700 text-white dark:border-emerald-500/40 dark:bg-emerald-600'
                                            : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-900'
                                    }`}
                                >
                                    Full outcome
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setPaymentResolutionMode('split')}
                                    className={`rounded-xl border px-3 py-1.5 text-[11px] font-semibold transition-all ${
                                        paymentResolutionMode === 'split'
                                            ? 'border-teal-600 bg-teal-700 text-white dark:border-emerald-500/40 dark:bg-emerald-600'
                                            : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-900'
                                    }`}
                                >
                                    Custom split
                                </button>
                            </div>

                            {escrowLoading ? (
                                <div className="mt-4 flex items-center gap-2 text-sm text-zinc-500">
                                    <Loader size="18px" stroke="#0d9488" />
                                    Loading escrow from chain…
                                </div>
                            ) : escrowContext ? (
                                <div className="mt-4 rounded-xl border border-zinc-200 bg-zinc-50/80 px-4 py-3 text-xs text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900/50 dark:text-zinc-400">
                                    <p>
                                        Remaining escrow:{' '}
                                        <span className="font-semibold text-zinc-900 dark:text-white">
                                            {escrowContext.remainingFormatted}
                                        </span>
                                        {' · '}
                                        Already claimed:{' '}
                                        <span className="font-medium">
                                            {escrowContext.claimedFormatted}
                                        </span>
                                        {' / '}
                                        {escrowContext.totalFormatted}
                                    </p>
                                </div>
                            ) : null}

                            {paymentResolutionMode === 'full' ? (
                                <div className="mt-4 flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={() => handlePaymentResolveOnChain(true)}
                                        disabled={onChainResolving !== null}
                                        className="rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-500 disabled:opacity-60"
                                    >
                                        {onChainResolving === 'payment-approve'
                                            ? 'Waiting for wallet…'
                                            : 'Approve refund to sender'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handlePaymentResolveOnChain(false)}
                                        disabled={onChainResolving !== null}
                                        className="rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-500 disabled:opacity-60"
                                    >
                                        {onChainResolving === 'payment-reject'
                                            ? 'Waiting for wallet…'
                                            : 'Deny refund (pay receiver)'}
                                    </button>
                                </div>
                            ) : (
                                <div className="mt-4 space-y-3">
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                        <div>
                                            <label className="mb-1 block text-xs font-semibold text-zinc-600 dark:text-zinc-400">
                                                Refund to sender
                                            </label>
                                            <input
                                                type="number"
                                                min={0}
                                                step="any"
                                                value={splitToSender}
                                                onChange={(e) =>
                                                    handleSplitSenderChange(
                                                        e.target.value,
                                                    )
                                                }
                                                placeholder="0.00"
                                                className="cpg-input w-full tabular-nums"
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-xs font-semibold text-zinc-600 dark:text-zinc-400">
                                                Pay receiver
                                            </label>
                                            <input
                                                type="number"
                                                min={0}
                                                step="any"
                                                value={splitToReceiver}
                                                onChange={(e) =>
                                                    handleSplitReceiverChange(
                                                        e.target.value,
                                                    )
                                                }
                                                placeholder="0.00"
                                                className="cpg-input w-full tabular-nums"
                                            />
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handlePaymentResolveWithSplit}
                                        disabled={
                                            onChainResolving !== null ||
                                            !escrowContext?.remainingWei ||
                                            BigInt(escrowContext?.remainingWei || 0) ===
                                                0n
                                        }
                                        className="rounded-lg bg-teal-700 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-teal-600 disabled:opacity-60 dark:bg-emerald-600 dark:hover:bg-emerald-500"
                                    >
                                        {onChainResolving === 'payment-split'
                                            ? 'Waiting for wallet…'
                                            : 'Resolve with split on-chain'}
                                    </button>
                                    <p className="text-[11px] text-zinc-500 dark:text-zinc-500">
                                        Calls{' '}
                                        <span className="font-mono">
                                            resolveDisputeWithSplit
                                        </span>
                                        . Sender + receiver shares must equal the
                                        remaining escrow exactly (fees apply on-chain).
                                    </p>
                                </div>
                            )}

                            {!dispute.disputeId && dispute.disputeId !== 0 ? (
                                <p className="mt-3 text-xs text-amber-700 dark:text-amber-300">
                                    This payment dispute is not linked to an on-chain ID yet.
                                    Wait for sync, then try again.
                                </p>
                            ) : null}
                            <ResolutionGuide
                                items={[
                                    {
                                        title: 'Full refund to sender',
                                        body: 'Uses resolveDispute(refund=true). Returns all remaining locked funds to the sender.',
                                    },
                                    {
                                        title: 'Full release to receiver',
                                        body: 'Uses resolveDispute(refund=false). Releases remaining escrow to the receiver path.',
                                    },
                                    {
                                        title: 'Custom split',
                                        body: 'Uses resolveDisputeWithSplit when work is partially complete. Split amounts must sum to the exact remaining escrow.',
                                    },
                                ]}
                            />
                        </>
                    )}
                </section>

                <div className="space-y-6 rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm dark:border-zinc-700 dark:bg-zinc-900/90">
                    {jobDispute ? (
                        <>
                            <DetailField label="Task">
                                {dispute.taskTitle ||
                                    dispute.description ||
                                    '—'}
                            </DetailField>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <DetailField label="Task ID">
                                    <span className="font-mono text-xs">
                                        {dispute.taskId || '—'}
                                    </span>
                                </DetailField>
                                <DetailField label="Escrow ID">
                                    <span className="font-mono text-xs">
                                        {dispute.taskEscrowId != null
                                            ? dispute.taskEscrowId
                                            : '—'}
                                    </span>
                                </DetailField>
                            </div>
                            <DetailField label="Raised by">
                                <span className="font-mono text-xs break-all">
                                    {dispute.raisedByWallet || '—'}
                                </span>
                            </DetailField>
                            {dispute.resolutionOutcome ? (
                                <DetailField label="Resolution">
                                    {dispute.resolutionOutcome === 'employee'
                                        ? 'Employee (funds released)'
                                        : 'Company (refund)'}
                                    {dispute.resolutionNote ? (
                                        <span className="mt-1 block text-sm text-zinc-600 dark:text-zinc-400">
                                            Note: {dispute.resolutionNote}
                                        </span>
                                    ) : null}
                                </DetailField>
                            ) : null}
                        </>
                    ) : (
                        <>
                            <DetailField label="On-chain Dispute ID">
                                {dispute.disputeId ?? '—'}
                            </DetailField>
                            <DetailField label="Payment ID">
                                {dispute.paymentId || '—'}
                            </DetailField>
                        </>
                    )}

                    {dispute.noDetails ? (
                        <div className="rounded-lg border border-amber-500/25 bg-amber-500/10 p-4">
                            <div className="mb-2 flex items-center gap-2">
                                <i
                                    className="fa-solid fa-triangle-exclamation text-amber-600 dark:text-amber-400"
                                    aria-hidden
                                />
                                <span className="font-medium text-amber-900 dark:text-amber-200">
                                    Details not available
                                </span>
                            </div>
                            <p className="text-sm text-amber-950/90 dark:text-amber-100/90">
                                {dispute.description}
                            </p>
                            <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-400">
                                Contact the user directly to request dispute
                                details.
                            </p>
                        </div>
                    ) : (
                        <>
                            <DetailField label="Description">
                                {dispute.description}
                            </DetailField>
                            <DetailField label="Reason">
                                {dispute.reason}
                            </DetailField>
                            {dispute.proof ? (
                                <DetailField label="Additional proof / details">
                                    <p className="whitespace-pre-wrap">
                                        {dispute.proof}
                                    </p>
                                </DetailField>
                            ) : null}
                            {dispute.filePath ? (
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-zinc-600 dark:text-zinc-400">
                                        Attachment
                                    </label>
                                    <a
                                        href={resolveAttachmentUrl(
                                            dispute.filePath,
                                        )}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm text-white transition-all hover:bg-teal-500"
                                    >
                                        <i className="fa-solid fa-file" />
                                        View attachment
                                    </a>
                                </div>
                            ) : null}
                            <div>
                                <label className="mb-1 block text-sm font-medium text-zinc-600 dark:text-zinc-400">
                                    Status
                                </label>
                                <ThemedSelect
                                    value={dispute.status}
                                    onChange={(e) =>
                                        handleStatusChange(e.target.value)
                                    }
                                    options={[
                                        {
                                            value: 'pending',
                                            label: 'Pending',
                                        },
                                        {
                                            value: 'in_review',
                                            label: 'In Review',
                                        },
                                        {
                                            value: 'resolved',
                                            label: 'Resolved',
                                        },
                                        {
                                            value: 'closed',
                                            label: 'Closed',
                                        },
                                    ]}
                                />
                            </div>
                        </>
                    )}
                </div>

                {dispute.id != null && (
                    <section className="mt-8">
                        <h2 className="mb-4 text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                            Discussion
                        </h2>
                        <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-700 dark:bg-zinc-900/90">
                            {disputeThreadAuthReady ? (
                                <DisputeMessageThread
                                    disputeId={dispute.id}
                                    authToken={disputeThreadToken}
                                    variant={isDark ? 'dark' : 'light'}
                                    discussionMode={
                                        dispute.taskId ? 'task' : 'payment'
                                    }
                                />
                            ) : (
                                <div className="flex justify-center py-12">
                                    <Loader size="32px" stroke="#0d9488" />
                                </div>
                            )}
                        </div>
                    </section>
                )}

                <footer className="mt-8 flex justify-end border-t border-zinc-200 pt-6 dark:border-zinc-700">
                    <Link
                        to={backHref}
                        className="rounded-lg border border-zinc-300 bg-zinc-100 px-4 py-2.5 text-sm font-medium text-zinc-800 transition-colors hover:bg-zinc-200 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
                    >
                        Back to queue
                    </Link>
                </footer>
            </div>
        </div>
    );
};

export default AdminDisputeDetailPage;
